import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, useAnimation, useInView } from 'framer-motion';
import { FaGithub, FaLinkedin, FaFileAlt } from 'react-icons/fa';

const Hero = () => {
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  // Text to be displayed in the typing animation
  const roles = ["Data Engineer", "Software Engineer", "Problem Solver"];

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <HeroSection ref={ref} id="hero">
      <div className="container">
        <HeroContent>
          <TextContainer>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Hello, I'm <Highlight>Amrit Prasad Phuyal</Highlight>
            </motion.h1>

            <RoleText
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <TypingText roles={roles} />
            </RoleText>

            <Description
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Data Engineer with expertise in building scalable data pipelines using PySpark,
              Databricks, Fabric, Azure, and AWS. Proven experience in real-time data processing,
              Data Quality frameworks, and end-to-end ELT development.
            </Description>

            <ButtonContainer
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <PrimaryButton href="#projects">View Projects</PrimaryButton>
              <SecondaryButton href="/assets/Amrit_Prasad_Phuyal_resume.pdf" target="_blank" rel="noopener noreferrer">
                <FaFileAlt style={{ marginRight: '10px' }} />
                Resume
              </SecondaryButton>
            </ButtonContainer>

            <SocialLinks
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <SocialLink href="https://github.com/amritphuyal" target="_blank" rel="noopener noreferrer">
                <FaGithub />
              </SocialLink>
              <SocialLink href="https://www.linkedin.com/in/amritphuyal/" target="_blank" rel="noopener noreferrer">
                <FaLinkedin />
              </SocialLink>
            </SocialLinks>
          </TextContainer>
        </HeroContent>
      </div>
    </HeroSection>
  );
};

// Typing Text Animation Component
const TypingText = ({ roles }) => {
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);
  const [blink, setBlink] = useState(true);

  // Typing effect
  useEffect(() => {
    if (subIndex === roles[index].length + 1 && !reverse) {
      setTimeout(() => setReverse(true), 2000);
      return;
    }

    if (subIndex === 0 && reverse) {
      setReverse(false);
      setIndex((prev) => (prev + 1) % roles.length);
      return;
    }

    const timeout = setTimeout(() => {
      setSubIndex((prev) => prev + (reverse ? -1 : 1));
    }, Math.max(reverse ? 75 : subIndex === roles[index].length ? 1000 : 100, parseInt(Math.random() * 40)));

    return () => clearTimeout(timeout);
  }, [subIndex, index, reverse, roles]);

  // Blinking cursor effect
  useEffect(() => {
    const timeout = setTimeout(() => {
      setBlink((prev) => !prev);
    }, 500);
    return () => clearTimeout(timeout);
  }, [blink]);

  return (
    <span>
      <HighlightTyping>
        {roles[index].substring(0, subIndex)}
        <Cursor className={blink ? 'blink' : ''}>|</Cursor>
      </HighlightTyping>
    </span>
  );
};

// Styled Components
const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  background-color: var(--primary);
  padding: 0 2rem;
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 800px;
`;

const TextContainer = styled.div`
  h1 {
    font-size: 4rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: var(--text-light);
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
    
    @media (max-width: 480px) {
      font-size: 2.5rem;
    }
  }
`;

const RoleText = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Description = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 1.2rem;
  max-width: 600px;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const Highlight = styled.span`
  color: var(--accent);
`;

const HighlightTyping = styled.span`
  color: var(--accent);
  font-weight: 600;
`;

const Cursor = styled.span`
  color: var(--accent);
  &.blink {
    opacity: 0;
  }
`;

const ButtonContainer = styled(motion.div)`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.a`
  padding: 0.8rem 1.8rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: var(--accent);
  color: var(--text-light);
  border: 2px solid var(--accent);
  box-shadow: 0 4px 10px rgba(30, 136, 229, 0.3);
  
  &:hover {
    background-color: transparent;
    color: var(--accent);
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(30, 136, 229, 0.4);
  }
`;

const SecondaryButton = styled(Button)`
  background-color: transparent;
  color: var(--accent);
  border: 2px solid var(--accent);
  
  &:hover {
    background-color: rgba(30, 136, 229, 0.1);
    transform: translateY(-2px);
  }
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  margin-top: 2.5rem;
`;

const SocialLink = styled.a`
  color: var(--text-secondary);
  font-size: 1.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--accent);
    transform: translateY(-3px);
  }
`;

export default Hero;
